import {
  OSMBuildingFeatureCollection,
  TileCoordinates,
  OSMBuildingFeature,
} from '@/types/osm';

// OSMBuildings tile server
// Note: The official tile server may have authentication requirements or rate limits
// Alternative: Use OSM data directly via Overpass API (handled in osmService.ts)
const OSMBUILDINGS_TILE_SERVER =
  'https://data.osmbuildings.org/0.2/anonymous/tile';

/**
 * Convert lat/lon coordinates to tile coordinates at given zoom level
 * @param lat - Latitude
 * @param lon - Longitude
 * @param zoom - Zoom level (typically 16-18 for building details)
 * @returns Tile coordinates
 */
export function latLonToTile(
  lat: number,
  lon: number,
  zoom: number
): TileCoordinates {
  const x = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
  const y = Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      Math.pow(2, zoom)
  );

  return { z: zoom, x, y };
}

/**
 * Fetch OSMBuildings GeoJSON data for a specific tile
 * @param tile - Tile coordinates
 * @returns GeoJSON FeatureCollection with building data
 */
export async function fetchOSMBuildingsTile(
  tile: TileCoordinates
): Promise<OSMBuildingFeatureCollection> {
  try {
    const url = `${OSMBUILDINGS_TILE_SERVER}/${tile.z}/${tile.x}/${tile.y}.json`;

    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // No buildings in this tile
        return {
          type: 'FeatureCollection',
          features: [],
        };
      }
      if (response.status === 403) {
        // Authentication required or rate limited
        console.warn('OSMBuildings tile server requires authentication or rate limited');
        return {
          type: 'FeatureCollection',
          features: [],
        };
      }
      throw new Error(`OSMBuildings API error: ${response.statusText}`);
    }

    const data: OSMBuildingFeatureCollection = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching OSMBuildings tile:', error);
    throw error;
  }
}

/**
 * Fetch OSMBuildings data for coordinates at specified zoom level
 * @param lat - Latitude
 * @param lon - Longitude
 * @param zoom - Zoom level (default 17 for building details)
 * @returns GeoJSON FeatureCollection with building data
 */
export async function fetchOSMBuildingsGeoJSON(
  lat: number,
  lon: number,
  zoom: number = 17
): Promise<OSMBuildingFeatureCollection> {
  const tile = latLonToTile(lat, lon, zoom);
  return fetchOSMBuildingsTile(tile);
}

/**
 * Find the building feature closest to given coordinates
 * @param geoJSON - GeoJSON FeatureCollection
 * @param lat - Target latitude
 * @param lon - Target longitude
 * @returns Closest building feature or null
 */
export function findClosestBuilding(
  geoJSON: OSMBuildingFeatureCollection,
  lat: number,
  lon: number
): OSMBuildingFeature | null {
  if (geoJSON.features.length === 0) {
    return null;
  }

  let closestFeature: OSMBuildingFeature | null = null;
  let minDistance = Infinity;

  for (const feature of geoJSON.features) {
    if (feature.geometry.type === 'Polygon') {
      // Calculate distance to polygon centroid
      const centroid = calculatePolygonCentroid(feature.geometry.coordinates[0]);
      const distance = calculateDistance(lat, lon, centroid.lat, centroid.lon);

      if (distance < minDistance) {
        minDistance = distance;
        closestFeature = feature;
      }
    }
  }

  return closestFeature;
}

/**
 * Calculate centroid of a polygon
 * @param coordinates - Array of [lon, lat] coordinates
 * @returns Centroid coordinates
 */
function calculatePolygonCentroid(
  coordinates: [number, number][]
): { lat: number; lon: number } {
  let sumLat = 0;
  let sumLon = 0;
  const count = coordinates.length;

  for (const [lon, lat] of coordinates) {
    sumLat += lat;
    sumLon += lon;
  }

  return {
    lat: sumLat / count,
    lon: sumLon / count,
  };
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param lat1 - First latitude
 * @param lon1 - First longitude
 * @param lat2 - Second latitude
 * @param lon2 - Second longitude
 * @returns Distance in meters
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Merge building properties from multiple sources (OSM + OSMBuildings)
 * @param osmProperties - Properties from OSM Overpass API
 * @param osmBuildingsProperties - Properties from OSMBuildings
 * @returns Merged properties with OSMBuildings taking precedence for visualization
 */
export function mergeBuildingProperties(
  osmProperties: any,
  osmBuildingsProperties: any
): any {
  return {
    ...osmProperties,
    ...osmBuildingsProperties,
    // Prefer OSMBuildings height if available
    height: osmBuildingsProperties.height || osmProperties.height,
    minHeight: osmBuildingsProperties.minHeight || osmProperties.minHeight || 0,
    // Prefer OSMBuildings colors for visualization
    color: osmBuildingsProperties.color || osmProperties.color || '#e8e0d8',
    roofColor:
      osmBuildingsProperties.roofColor ||
      osmProperties.roofColor ||
      '#c8bdb0',
  };
}
